import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';

import useSWR from 'swr';

import authSessionManager from '../auth/sessionManager';

import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';

import SideBar from '../components/SideBar';
import Logo from '../components/Logo';
import CanadaBeefLogo from '../shared/svg/canada-beef-logo.svg';
import { Button, IconButton, Typography } from '@mui/material';
import { Menu } from '@mui/icons-material';
import UserMenu from '../components/shared/UserMenu';
import { insightsDesignEnabled } from '../common/utils/insightsDesignEnabled';
import { paper, stone } from '../routes/pricing-reports/design/designTokens';
import { useCheckRetailerSet } from '../common/hooks/useCheckRetailerSet';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { AuthenticatedMenuContext } from './authenticatedMenuContext';

function Authenticated(props) {
    const location = useLocation();
    const retailerSet = useCheckRetailerSet();

    const { window: containerWindow, children, language, setLanguage } = props;
    const [mobileOpen, setMobileOpen] = useState(false);
    const [authHydrated, setAuthHydrated] = useState(false);

    const user = useSelector(state => state.user);

    useEffect(() => {
        let cancelled = false;

        const hydrateSession = async () => {
            try {
                authSessionManager.init();
                authSessionManager.syncFromStore();
                await authSessionManager.hydrateSession();
            } catch {
                // Session manager handles redirect decisions for definitive invalid sessions.
            } finally {
                if (!cancelled) {
                    authSessionManager.startSchedulers();
                    setAuthHydrated(true);
                }
            }
        };

        hydrateSession();
        return () => {
            cancelled = true;
            authSessionManager.stopSchedulers();
            authSessionManager.dispose();
        };
    }, []);

    const { data: defaults } = useSWR(
        authHydrated ? { url: 'defaults' } : null
    );

    let drawerWidth = 0;

    if (defaults) {
        drawerWidth = 336;
    }

    const designDrawerPaperChromeSx = insightsDesignEnabled()
        ? {
            backgroundColor: paper,
            borderRight: `1px solid ${stone}`,
        }
        : {};

    const mobileDrawerPaperSx = {
        boxSizing: 'border-box',
        width: '80%',
        ...designDrawerPaperChromeSx,
    };

    const permanentDrawerPaperSx = {
        boxSizing: 'border-box',
        width: drawerWidth,
        ...designDrawerPaperChromeSx,
    };

    const drawerFooterSx = {
        position: 'absolute',
        bottom: 0,
        width: '100%',
        px: 2,
        py: 2,
        ...(insightsDesignEnabled()
            ? { borderTop: `1px solid ${stone}` }
            : { borderTop: '1px solid #e0e0e0' }),
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const drawer = (
        <Box>
            <Box sx={{ width: '100%', position: 'relative' }}>
                <Stack
                    sx={{
                        py: 2,
                        px: 2,
                        background: 'transparent'
                    }}
                    direction="row"
                    spacing={1}
                    alignItems={'center'}
                    justifyContent={'center'}
                >
                    <Logo />
                </Stack>
            </Box>
            {user?.access_token &&
                <Box sx={{ px: 2 }}>
                    <SideBar />
                </Box>
            }
        </Box>
    );

    const container = containerWindow !== undefined ? () => containerWindow().document.body : undefined;

    const hasAuthSession = Boolean(
        (user?.access_token && user.access_token.length > 0) ||
        (user?.refresh_token && user.refresh_token.length > 0)
    );

    // Logged-out users have no retailer; send them to login instead of the "no partner" message.
    if (!retailerSet && !hasAuthSession) {
        const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
        return <Navigate to={`/login?redirect=${redirect}`} replace />;
    }

    // TODO extract to a component
    if (!retailerSet) {
        return (
            <Stack
                justifyContent={'center'}
                sx={{
                    height: '100vh',
                }}
            >
                <img
                    src={CanadaBeefLogo}
                    alt="img"
                    className="logo_img"
                    style={{ zIndex: 1, position: "relative" }}
                />
                
                <Typography
                    sx={{
                        textAlign: 'center',
                        mt: 1
                    }}
                >
                    Your account is not associated to a Canada Beef Partner and therefore can not use this application.
                </Typography>

                <Typography
                    sx={{
                        textAlign: 'center',
                        mt: 1
                    }}
                >
                    <a href='/login'>
                        Back to login
                    </a>
                </Typography>
            </Stack>
        )
    }

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            
            <Box
                component="nav"
                sx={{
                    width: {
                        md: drawerWidth,
                    },
                }}
                aria-label="mailbox folders"
            >
                <Drawer
                    container={container}
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: {
                            xs: 'block',
                            md: 'none'
                        },
                        '& .MuiDrawer-paper': mobileDrawerPaperSx,
                    }}
                >
                    {drawer}

                    <Box sx={drawerFooterSx}>
                        <UserMenu />
                    </Box>
                </Drawer>

                <Drawer
                    variant="permanent"
                    sx={{
                        display: {
                            xs: 'none',
                            md: 'block'
                        },
                        '& .MuiDrawer-paper': permanentDrawerPaperSx,
                    }}
                    open
                >
                    {drawer}

                    <Box sx={drawerFooterSx}>
                        <UserMenu />
                    </Box>
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    overflowX: 'hidden',
                    width: {
                        xs: '100%',
                        md: `calc(100% - ${drawerWidth}px)`
                    }
                }}
            >

                {
                    !!!defaults ?
                    <Stack
                        justifyContent={'center'}
                        sx={{
                            height: '100vh',
                        }}
                    >
                        <img
                            src={CanadaBeefLogo}
                            alt="img"
                            className="logo_img"
                            style={{ zIndex: 1, position: "relative" }}
                        />
                    </Stack> :
                    <>
                        <Box
                            sx={{
                                background: '#000',
                                px: 1,
                                py: 1,
                                display: {
                                    md: 'none',
                                },
                            }}
                        >
                            <Stack
                                direction={'row'}
                                alignItems={'center'}
                                justifyContent={'space-between'}
                            >
                                <Button
                                    onClick={handleDrawerToggle}
                                    sx={{
                                        // ...(mobileOpen && { display: 'none' }),
                                        color: '#fff',
                                    }}
                                    startIcon={<Menu />}
                                >
                                    Menu
                                </Button>

                                <LanguageSwitcher
                                    variant='compact'
                                    selectedLanguageProp={language}
                                    setSelectedLanguage={setLanguage}
                                />
                            </Stack>
                        </Box>
                        
                        <Box
                            sx={{
                                px: {
                                    xs: 1,
                                    sm: 2,
                                    md: 3,
                                },
                                py: 2,
                            }}
                        >
                            <AuthenticatedMenuContext.Provider value={handleDrawerToggle}>
                                {children}
                            </AuthenticatedMenuContext.Provider>
                        </Box>
                    </>
                }
            </Box>
        </Box>
    );
}

export default Authenticated;
